import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useApiClient } from '../ApiProvider';
import { unwrap, type ApiClient } from '../client';
import type { components } from '../schema.gen';
import { queryKeys } from './keys';

export type Upload = components['schemas']['Upload'];
export type UploadSummary = components['schemas']['UploadSummary'];
export type UploadPage = components['schemas']['UploadPage'];
export type UploadStatus = components['schemas']['UploadStatus'];
export type UploadKind = components['schemas']['UploadKind'];
export type PayDateSummary = components['schemas']['PayDateSummary'];
export type PayDatePage = components['schemas']['PayDatePage'];
export type UploadDeletionPreview = components['schemas']['UploadDeletionPreview'];
export type PendingPaySummary = components['schemas']['PendingPaySummary'];

/** While an upload is queued or processing, its status is polled (ADR 0020). */
export const POLL_MS = 2000;

export const isActive = (status: UploadStatus) => status === 'queued' || status === 'processing';

export interface UploadQuery {
  kind: UploadKind;
  q?: string | undefined;
  status?: UploadStatus | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page: number;
  pageSize: number;
}

/** A page of uploads, newest first, refetched every 2 s while any is queued or processing. */
export function useUploads({ kind, q, status, from, to, page, pageSize }: UploadQuery) {
  const api = useApiClient();
  const query = {
    kind,
    ...(q ? { q } : {}),
    ...(status ? { status } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    page,
    pageSize,
  };
  return useQuery({
    queryKey: queryKeys.adminUploadPage(query),
    queryFn: () => unwrap(api.GET('/api/admin/uploads', { params: { query } })),
    placeholderData: keepPreviousData,
    refetchInterval: (current) =>
      current.state.data?.items.some((upload) => isActive(upload.status)) ? POLL_MS : false,
  });
}

export interface PayDateQuery {
  q?: string | undefined;
  from?: string | undefined;
  to?: string | undefined;
  page: number;
  pageSize: number;
}

/** A page of pay dates, newest first, with each upload's contribution. */
export function usePayDates({ q, from, to, page, pageSize }: PayDateQuery, enabled = true) {
  const api = useApiClient();
  const query = {
    ...(q ? { q } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    page,
    pageSize,
  };
  return useQuery({
    queryKey: queryKeys.payDatePage(query),
    queryFn: () => unwrap(api.GET('/api/admin/pay-dates', { params: { query } })),
    placeholderData: keepPreviousData,
    enabled,
  });
}

function uploadQuery(api: ApiClient, id: string) {
  return queryOptions({
    queryKey: queryKeys.adminUpload(id),
    queryFn: () =>
      unwrap(api.GET('/api/admin/uploads/{upload_id}', { params: { path: { upload_id: id } } })),
    refetchInterval: (current) =>
      current.state.data && isActive(current.state.data.status) ? POLL_MS : false,
  });
}

/** One upload (its error report), polled while it's queued or processing. */
export function useUpload(id: string | null) {
  const api = useApiClient();
  return useQuery({ ...uploadQuery(api, id ?? ''), enabled: id !== null });
}

/** Several uploads, each polled while it's queued or processing. */
export function useUploadsById(ids: readonly string[]) {
  const api = useApiClient();
  return useQueries({ queries: ids.map((id) => uploadQuery(api, id)) });
}

/** Stores one file and queues it (one request per file), then refreshes the lists and counts. */
export function useCreateUpload() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      file,
      kind,
      pendingPayDate,
    }: {
      file: File;
      kind: UploadKind;
      pendingPayDate?: string;
    }) => {
      const body = new FormData();
      body.append('file', file);
      body.append('kind', kind);
      if (pendingPayDate) body.append('pendingPayDate', pendingPayDate);
      return unwrap(
        api.POST('/api/admin/uploads', {
          // A FormData body is sent as is, with its multipart boundary.
          body: body as unknown as { file: string; kind: UploadKind },
        }),
      );
    },
    onSuccess: (upload) => {
      queryClient.setQueryData(queryKeys.adminUpload(upload.id), upload);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.adminUploads() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.navCounts() }),
      ]);
    },
  });
}

/** What deleting the upload would remove (the API's dry run). */
export function useDeletionPreview(id: string | null) {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.deletionPreview(id ?? ''),
    queryFn: () =>
      unwrap(
        api.GET('/api/admin/uploads/{upload_id}/deletion-preview', {
          params: { path: { upload_id: id! } },
        }),
      ),
    enabled: id !== null,
    // Always the current numbers: the confirmation states exactly what goes.
    staleTime: 0,
    gcTime: 0,
  });
}

/** Deletes an upload, then refreshes the uploads, pay dates, pay views and nav counts. */
export function useDeleteUpload() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.DELETE('/api/admin/uploads/{upload_id}', { params: { path: { upload_id: id } } })),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.adminUploads() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.pay() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.navCounts() }),
      ]),
  });
}

/** The file name a download's Content-Disposition gives (RFC 6266's filename* first). */
export function fileNameOf(disposition: string | null, fallback: string) {
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition ?? '');
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed encoding: fall back to the plain name.
    }
  }
  return /filename="([^"]+)"/.exec(disposition ?? '')?.[1] ?? fallback;
}

export interface Download {
  blob: Blob;
  fileName: string;
}

/** An upload's original file, as uploaded. */
export function useDownloadOriginal() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (upload: { id: string; fileName: string }): Promise<Download> => {
      const result = await api.GET('/api/admin/uploads/{upload_id}/original', {
        params: { path: { upload_id: upload.id } },
        parseAs: 'blob',
      });
      const blob = await unwrap(Promise.resolve(result));
      const disposition = result.response.headers.get('Content-Disposition');
      return { blob, fileName: fileNameOf(disposition, upload.fileName) };
    },
  });
}

/** Pay history as .xlsx: every product type, optionally a pay-date range. */
export function useExportPayHistory() {
  const api = useApiClient();
  return useMutation({
    mutationFn: async (range: { from?: string | undefined; to?: string | undefined }) => {
      const query = {
        ...(range.from ? { from: range.from } : {}),
        ...(range.to ? { to: range.to } : {}),
      };
      const result = await api.GET('/api/admin/pay-history/export', {
        params: { query },
        parseAs: 'blob',
      });
      const blob = await unwrap(Promise.resolve(result));
      const disposition = result.response.headers.get('Content-Disposition');
      return { blob, fileName: fileNameOf(disposition, 'payhistory.xlsx') } satisfies Download;
    },
  });
}

/** The pending pay date and what is loaded for it (also what clearing removes). */
export function usePendingSummary() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.pendingSummary(),
    queryFn: () => unwrap(api.GET('/api/admin/pending-pay')),
  });
}

/** Clears pending pay, then refreshes the uploads, pay views and nav counts. */
export function useClearPendingPay() {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/admin/pending-pay')),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.adminUploads() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.pay() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.navCounts() }),
      ]),
  });
}
